// Folds a list of `JsonColumnDeclaration` (from MetaInfo) into a per-table
// index keyed by the engine's natural fold (`foldFor(engine)`): table name →
// one entry per declared schema → the declared column names. Lookups in the
// projector layer pass the projected relation (`qualified`) and check
// `fold(column.name)` membership (see `schemaProjectionHelpers.columnsFor`).
//
// A declaration applies to the relation of ITS schema only, so a declaration
// on `mia_s1.orders` never classifies `mia_s2.orders.meta`. A declaration
// without a schema (`''` — SQLite / MySQL legacy) applies to every
// same-named relation, and a schemaless relation (SQLite) takes every
// same-named declaration: `sameRelation` semantics.

import type { SqlEngine } from '../../sql-grammar/model';
import type { JsonColumnDeclaration } from '@/types/electron';
import { foldFor } from '../service/schemaProjectionHelpers';
import { sameRelation, type RelationIdentity } from '../service/snapshotRelations';

export type LooseJsonHintEntry = {
  readonly foldedSchema?: string;
  readonly columns: ReadonlySet<string>;
};

export type LooseJsonHintIndex = ReadonlyMap<string, readonly LooseJsonHintEntry[]>;

const EMPTY_INDEX: LooseJsonHintIndex = new Map();

export function buildLooseJsonHintIndex(
  decls: readonly JsonColumnDeclaration[] | undefined,
  engine: SqlEngine,
): LooseJsonHintIndex {
  if (!decls || decls.length === 0) return EMPTY_INDEX;
  const fold = foldFor(engine);
  const out = new Map<string, { foldedSchema?: string; columns: Set<string> }[]>();
  for (const d of decls) {
    const tKey = fold(d.table);
    const foldedSchema = d.schema ? fold(d.schema) : undefined;
    let entries = out.get(tKey);
    if (!entries) {
      entries = [];
      out.set(tKey, entries);
    }
    let entry = entries.find((e) => e.foldedSchema === foldedSchema);
    if (!entry) {
      entry = { foldedSchema, columns: new Set() };
      entries.push(entry);
    }
    entry.columns.add(fold(d.column));
  }
  return out;
}

export function lookupLooseJsonColumns(
  index: LooseJsonHintIndex | undefined,
  relation: RelationIdentity,
): ReadonlySet<string> | undefined {
  if (!index || index.size === 0) return undefined;
  const entries = index.get(relation.foldedName);
  if (!entries) return undefined;
  const matching = entries.filter((e) =>
    sameRelation({ foldedName: relation.foldedName, foldedSchema: e.foldedSchema }, relation),
  );
  if (matching.length === 0) return undefined;
  if (matching.length === 1) return matching[0].columns;
  return new Set(matching.flatMap((e) => [...e.columns]));
}
